import { useCallback, useEffect, useRef, useState } from "react"

const VIEWPORT_PADDING = 12

const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

const getBounds = (width, height, padding = VIEWPORT_PADDING) => {
  const maxX = Math.max(padding, window.innerWidth - width - padding)
  const maxY = Math.max(padding, window.innerHeight - height - padding)
  return {
    minX: padding,
    minY: padding,
    maxX,
    maxY,
  }
}

export const getDefaultMiniPosition = (
  width,
  height,
  padding = VIEWPORT_PADDING,
) => {
  const bounds = getBounds(width, height, padding)
  return { x: bounds.maxX, y: bounds.maxY }
}

export const clampPosition = (
  x,
  y,
  width,
  height,
  padding = VIEWPORT_PADDING,
) => {
  const bounds = getBounds(width, height, padding)
  return {
    x: clamp(x, bounds.minX, bounds.maxX),
    y: clamp(y, bounds.minY, bounds.maxY),
  }
}

/**
 * Pointer-based drag with viewport clamping. Distinguishes a click from a drag
 * so the mini player can restore on click without fighting repositioning.
 * Uses live node size when available so auto-height mini cards stay in bounds.
 */
const useDraggablePosition = ({
  enabled = true,
  size,
  padding = VIEWPORT_PADDING,
}) => {
  const nodeRef = useRef(null)
  const dragRef = useRef(null)
  const lastWasDragRef = useRef(false)
  const [position, setPosition] = useState(null)
  const [isDragging, setIsDragging] = useState(false)
  const [measuredSize, setMeasuredSize] = useState(null)

  const width = measuredSize?.width || size?.width || 320
  const height = measuredSize?.height || size?.height || 300

  const measureNode = useCallback(() => {
    const node = nodeRef.current
    if (!node) return null
    const rect = node.getBoundingClientRect()
    if (!rect.width || !rect.height) return null
    return {
      width: Math.ceil(rect.width),
      height: Math.ceil(rect.height),
    }
  }, [])

  useEffect(() => {
    if (!enabled) {
      setMeasuredSize(null)
      return undefined
    }

    const node = nodeRef.current
    if (!node || typeof ResizeObserver === "undefined") {
      const next = measureNode()
      if (next) setMeasuredSize(next)
      return undefined
    }

    const observer = new ResizeObserver(() => {
      const next = measureNode()
      if (next) setMeasuredSize(next)
    })
    observer.observe(node)
    const next = measureNode()
    if (next) setMeasuredSize(next)

    return () => observer.disconnect()
  }, [enabled, measureNode])

  useEffect(() => {
    if (!enabled) return undefined

    setPosition(prev => {
      const next = prev || getDefaultMiniPosition(width, height, padding)
      return clampPosition(next.x, next.y, width, height, padding)
    })
  }, [enabled, width, height, padding])

  useEffect(() => {
    if (!enabled) return undefined

    const handleResize = () => {
      setPosition(prev => {
        const fallback = getDefaultMiniPosition(width, height, padding)
        const current = prev || fallback
        return clampPosition(current.x, current.y, width, height, padding)
      })
    }

    window.addEventListener("resize", handleResize)
    return () => window.removeEventListener("resize", handleResize)
  }, [enabled, width, height, padding])

  const onPointerDown = useCallback(
    event => {
      if (!enabled || event.button !== 0) return
      if (document.body.classList.contains("session-guard-modal-open")) return
      if (event.target?.closest?.("[data-pip-no-drag]")) return

      const node = nodeRef.current
      if (!node) return

      const rect = node.getBoundingClientRect()
      dragRef.current = {
        pointerId: event.pointerId,
        offsetX: event.clientX - rect.left,
        offsetY: event.clientY - rect.top,
        startX: event.clientX,
        startY: event.clientY,
        moved: false,
        width: Math.ceil(rect.width),
        height: Math.ceil(rect.height),
      }

      try {
        node.setPointerCapture(event.pointerId)
      } catch {
        // ignore browsers that reject capture
      }
    },
    [enabled],
  )

  const onPointerMove = useCallback(
    event => {
      const drag = dragRef.current
      if (!drag || drag.pointerId !== event.pointerId) return

      const deltaX = Math.abs(event.clientX - drag.startX)
      const deltaY = Math.abs(event.clientY - drag.startY)
      if (!drag.moved && deltaX < 4 && deltaY < 4) return

      drag.moved = true
      if (!isDragging) setIsDragging(true)

      const dragWidth = drag.width || width
      const dragHeight = drag.height || height
      const next = clampPosition(
        event.clientX - drag.offsetX,
        event.clientY - drag.offsetY,
        dragWidth,
        dragHeight,
        padding,
      )
      setPosition(next)
    },
    [height, isDragging, padding, width],
  )

  const onPointerUp = useCallback(event => {
    const drag = dragRef.current
    if (!drag || drag.pointerId !== event.pointerId) return

    lastWasDragRef.current = drag.moved
    dragRef.current = null
    setIsDragging(false)

    const node = nodeRef.current
    if (node) {
      try {
        node.releasePointerCapture(event.pointerId)
      } catch {
        // ignore
      }
    }
  }, [])

  const consumeWasDrag = useCallback(() => {
    const wasDrag = lastWasDragRef.current
    lastWasDragRef.current = false
    return wasDrag
  }, [])

  return {
    nodeRef,
    position,
    isDragging,
    onPointerDown,
    onPointerMove,
    onPointerUp,
    consumeWasDrag,
  }
}

export default useDraggablePosition
